// frontend/src/pages/dashboards/DashboardsPage.jsx
// Phase 4 step 14a-3: the list of the organization's dashboards, at
// /dashboards. Everyone sees every dashboard; admins and analysts get
// "New dashboard", which asks for a title and opens the new dashboard in
// edit mode's doorstep (its page, empty, with Edit ready).
// Step 14c-3: a dashboard with a working public link shows "Shared publicly".
// Step 14d-3: the list holds the dashboards this person may open (the server
// filters it); a restricted one shows "Only specific people" with a lock.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog } from 'radix-ui';
import { LayoutDashboard, Loader2, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { EmptyState } from '@/components/ui/empty-state.jsx';
import { Panel } from '@/components/ui/panel.jsx';
import { createDashboard, listDashboards } from '../../charts/semanticApi';
import { MAX_DESCRIPTION, MAX_TITLE, cleanText, tileCountText, updatedText } from './dashboardState';
import SharedTag from './SharedTag';
import RestrictedTag from './RestrictedTag';

const FIELD = 'w-full rounded-xl bg-[var(--ui-inset)] px-3 text-[13px] text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30';

/** The dashboards matching a search, newest change first (the server's order). */
export function filterDashboards(list, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return list;
  return list.filter((d) => (d.title || '').toLowerCase().includes(q) || (d.description || '').toLowerCase().includes(q));
}

/** One card of the list: title, description, tile count and last change, and the restricted and public tags. */
export function DashboardCard({ dashboard: d }) {
  return (
    <Link to={`/dashboards/${d.id}`}
      className="block h-full rounded-[22px] bg-[var(--ui-panel)] p-5 text-[var(--ui-text)] shadow-[inset_0_1px_0_var(--ui-panel-highlight)] transition-colors hover:bg-[#181818] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
      <span className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className="block truncate text-[15px] font-semibold tracking-[-0.01em]">{d.title}</span>
          {d.description && <span className="mt-1 line-clamp-2 block text-[13px] text-[var(--ui-text-muted)]">{d.description}</span>}
        </span>
        <LayoutDashboard className="mt-0.5 h-4 w-4 shrink-0 text-[var(--ui-text-muted)]" aria-hidden="true" />
      </span>
      <span className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--ui-text-muted)]">
        <span>{tileCountText(d.tile_count)}{updatedText(d.updated_at, d.created_by) ? ` · ${updatedText(d.updated_at, d.created_by)}` : ''}</span>
        {d.access === 'restricted' && <RestrictedTag />}
        {d.shared === true && <SharedTag />}
      </span>
    </Link>
  );
}

function NewDashboardDialog({ open, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ref = useRef(null);
  useEffect(() => { if (open) { setTitle(''); setDescription(''); setError(''); setBusy(false); } }, [open]);
  const clean = cleanText(title, MAX_TITLE, { oneLine: true });

  const submit = async (e) => {
    e.preventDefault();
    if (!clean || busy) return;
    setBusy(true); setError('');
    try {
      const d = await createDashboard({ title: clean, description: cleanText(description, MAX_DESCRIPTION) || null });
      onCreated(d);
    } catch (err) {
      setError(err && err.status === 403 ? "You don't have permission to create dashboards."
        : err && err.code === 'DASHBOARD_LIMIT' ? err.message : "The dashboard couldn't be created. Try again.");
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next && !busy) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          onOpenAutoFocus={(e) => { e.preventDefault(); if (ref.current) ref.current.focus(); }}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-[22px] bg-[var(--ui-panel)] p-6 text-[var(--ui-text)] shadow-[0_24px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/5 focus:outline-none"
        >
          <form onSubmit={submit}>
            <Dialog.Title className="mb-1 text-lg font-semibold tracking-[-0.01em]">New dashboard</Dialog.Title>
            <Dialog.Description className="mb-4 text-sm text-[var(--ui-text-muted)]">
              It starts empty. Add charts and text on its page.
            </Dialog.Description>
            <label className="mb-1 block text-xs text-[var(--ui-text-muted)]" htmlFor="new-dashboard-title">Title</label>
            <input id="new-dashboard-title" ref={ref} type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={MAX_TITLE}
              placeholder="Operations" className={`${FIELD} mb-3 h-9`} />
            <label className="mb-1 block text-xs text-[var(--ui-text-muted)]" htmlFor="new-dashboard-description">Description (optional)</label>
            <textarea id="new-dashboard-description" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={MAX_DESCRIPTION} rows={2}
              className={`${FIELD} resize-y py-2`} />
            {error && <p className="mt-2 text-xs text-red-400" role="alert">{error}</p>}
            <div className="mt-4 flex items-center justify-end gap-3">
              <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button type="submit" disabled={!clean || busy}>
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />} Create
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default function DashboardsPage() {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', dashboards: [], canEdit: false, error: null });
  const [attempt, setAttempt] = useState(0);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    setState((s) => ({ ...s, status: 'loading', error: null }));
    listDashboards({ signal: ctrl.signal })
      .then((body) => { if (!ctrl.signal.aborted) setState({ status: 'ready', dashboards: body.dashboards || [], canEdit: Boolean(body.can_edit), error: null }); })
      .catch((error) => { if (!ctrl.signal.aborted) setState({ status: 'error', dashboards: [], canEdit: false, error }); });
    return () => ctrl.abort();
  }, [attempt]);

  const shown = useMemo(() => filterDashboards(state.dashboards, query), [state.dashboards, query]);

  return (
    <div className="flex flex-col gap-4">
      <Panel className="py-3.5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold tracking-[-0.01em] text-[var(--ui-text)]">Dashboards</h2>
            <p className="mt-0.5 text-xs text-[var(--ui-text-muted)]">Saved charts arranged on a page, for your organization or for the people you choose.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ui-text-muted)]" aria-hidden="true" />
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search dashboards" aria-label="Search dashboards"
                className={`${FIELD} h-9 w-56 pl-9`} />
            </div>
            {state.canEdit && (
              <Button size="sm" onClick={() => setCreating(true)}><Plus className="h-3.5 w-3.5" aria-hidden="true" /> New dashboard</Button>
            )}
          </div>
        </div>
      </Panel>

      {state.status === 'loading' && (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-[var(--ui-text-muted)]" role="status">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading dashboards
        </div>
      )}
      {state.status === 'error' && (
        <Panel className="flex flex-col items-center gap-3 py-12 text-center">
          <p className="text-sm text-[var(--ui-text-muted)]">The dashboards couldn't be loaded.</p>
          <Button variant="secondary" size="sm" onClick={() => setAttempt((n) => n + 1)}>Try again</Button>
        </Panel>
      )}
      {state.status === 'ready' && state.dashboards.length === 0 && (
        <Panel>
          <EmptyState
            icon={LayoutDashboard} title="No dashboards yet"
            description={state.canEdit
              ? 'A dashboard puts saved charts side by side on one page. Make one, then add charts from your library.'
              : 'A dashboard puts saved charts side by side on one page. An admin or analyst can make the first one.'}
            action={state.canEdit ? <Button onClick={() => setCreating(true)}><Plus className="h-4 w-4" aria-hidden="true" /> New dashboard</Button> : null}
          />
        </Panel>
      )}
      {state.status === 'ready' && state.dashboards.length > 0 && shown.length === 0 && (
        <Panel className="py-12 text-center text-sm text-[var(--ui-text-muted)]">No dashboard matches that.</Panel>
      )}
      {shown.length > 0 && (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-label="Dashboards">
          {shown.map((d) => (
            <li key={d.id}><DashboardCard dashboard={d} /></li>
          ))}
        </ul>
      )}

      <NewDashboardDialog open={creating} onClose={() => setCreating(false)} onCreated={(d) => navigate(`/dashboards/${d.id}`)} />
    </div>
  );
}
